import axios from 'axios';
import {
  getAuthAdapter,
  getActiveProductLineId,
  getProductLineDeclarations,
} from '../../../adapters';
import {
  setSolutionsCatalog,
  setScopedSolutionsCatalog,
} from '../../../shell/events/solutionsCatalogBridge';
import type { CatalogItemSnapshot } from '../../../shell/events/solutionsCatalogBridge';

interface ModuleSolutionMapItem {
  module_ukey: string;
  solution_ukey: string;
}

interface PksEndpointInfo {
  baseUrl: string;
  platformTenant: string;
}

export function derivePksEndpoint(): PksEndpointInfo | null {
  try {
    const plId = getActiveProductLineId();
    const declarations = getProductLineDeclarations();
    const pl = declarations.find(d => d.id === plId) ?? declarations[0];
    if (!pl?.auth.serviceUrl) return null;

    const url = new URL(pl.auth.serviceUrl);
    const segments = url.pathname.split('/').filter(Boolean);
    const platformTenant = segments[0] ?? '';
    const pksPath = url.pathname.replace(/\/auth-service\//, '/platform-knowledge-service/');

    const isLocalDev = window.RUNTIME_CONFIG?.UI_ENV === 'local';
    const baseUrl = isLocalDev
      ? pksPath.replace(/\/$/, '')
      : `${url.origin}${pksPath}`.replace(/\/$/, '');

    return { baseUrl, platformTenant };
  } catch {
    return null;
  }
}

export function buildPksHeaders(platformTenant: string): Record<string, string> {
  const auth = getAuthAdapter();
  const token = auth.getAccessToken();
  const userId = auth.getIdentity()?.user.id;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (platformTenant) headers['x-tenant-id'] = platformTenant;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

export function buildCatalogParams(
  tenantId: string,
  applicationUkey?: string,
): URLSearchParams {
  const params = new URLSearchParams({
    tenant_ukey: tenantId,
    subscription: 'true',
    runtime_agent: 'true',
    linked_agents: 'false',
  });
  if (applicationUkey) {
    params.set('application_ukey', applicationUkey);
  }
  return params;
}

export function unwrapArray<T>(envelope: unknown): T[] {
  if (Array.isArray(envelope)) return envelope as T[];
  if (envelope && typeof envelope === 'object') {
    const obj = envelope as Record<string, unknown>;
    if (obj.status === 'error') return [];
    if (Array.isArray(obj.response)) return obj.response as T[];
    if (obj.response && typeof obj.response === 'object') {
      const resp = obj.response as Record<string, unknown>;
      if (Array.isArray(resp.items)) return resp.items as T[];
    }
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (Array.isArray(obj.items)) return obj.items as T[];
  }
  return [];
}

/** Fetch catalog without application_ukey and publish the waffle bridge snapshot. */
export async function fetchAndPublishUnscopedCatalog(
  tenantId: string,
  moduleKeys: string[],
): Promise<void> {
  const pksEndpoint = derivePksEndpoint();
  if (!pksEndpoint) return;

  const headers = buildPksHeaders(pksEndpoint.platformTenant);
  const params = buildCatalogParams(tenantId);

  const catalogResponse = await axios.get<unknown>(
    `${pksEndpoint.baseUrl}/solutions/catalog`,
    { headers, params },
  );
  const catalogItems = unwrapArray<CatalogItemSnapshot>(catalogResponse.data);

  const moduleResults = await Promise.allSettled(
    moduleKeys.map(async (moduleKey) => {
      const resp = await axios.get<unknown>(
        `${pksEndpoint.baseUrl}/modules/${encodeURIComponent(moduleKey)}/solutions`,
        { headers },
      );
      return { moduleKey, maps: unwrapArray<ModuleSolutionMapItem>(resp.data) };
    }),
  );

  const moduleSolutionMap: Record<string, string[]> = {};
  for (const result of moduleResults) {
    if (result.status !== 'fulfilled') continue;
    const { moduleKey, maps } = result.value;
    moduleSolutionMap[moduleKey] = maps.map(m => m.solution_ukey);
  }

  setSolutionsCatalog({ catalogItems, moduleSolutionMap });
}

/**
 * Fetch catalog WITH application_ukey and publish the scoped bridge for Runs.
 * Always issues a network request — call on every waffle selection.
 *
 * Publishes an empty placeholder for the target application first so canvas
 * consumers drop the previous app's options immediately (avoid stale Runs list).
 */
export async function fetchAndPublishScopedCatalog(
  tenantId: string,
  applicationUkey: string,
): Promise<void> {
  if (!tenantId || !applicationUkey) return;

  setScopedSolutionsCatalog({ applicationUkey, catalogItems: [] });

  const pksEndpoint = derivePksEndpoint();
  if (!pksEndpoint) return;

  const headers = buildPksHeaders(pksEndpoint.platformTenant);
  const params = buildCatalogParams(tenantId, applicationUkey);

  const catalogResponse = await axios.get<unknown>(
    `${pksEndpoint.baseUrl}/solutions/catalog`,
    { headers, params },
  );

  const catalogItems = unwrapArray<CatalogItemSnapshot>(catalogResponse.data);
  setScopedSolutionsCatalog({ applicationUkey, catalogItems });
}
